import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { VenuePublicDeal } from '@/lib/api/apiTypes';
import { formatDealDays, formatDealTimeRange, getDealTiming } from './deals';
import { cn } from '@/lib/utils';
import {
  BookmarkIcon,
  CheckIcon,
  Clock3Icon,
  TicketPercentIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { View } from 'react-native';

const PREVIEW_DEAL_COUNT = 3;
const WEEK_IN_MINUTES = 7 * 24 * 60;

export function VenueDealsPreview({
  deals,
  isSavingDeal,
  onSave,
}: {
  deals: VenuePublicDeal[];
  isSavingDeal?: boolean;
  onSave?: (deal: VenuePublicDeal) => void;
}) {
  const [showAll, setShowAll] = React.useState(false);
  const dealsThisWeek = React.useMemo(
    () =>
      deals.filter((deal) => {
        const timing = getDealTiming(deal);
        return timing.state === 'live' || timing.minutesUntil <= WEEK_IN_MINUTES;
      }),
    [deals]
  );
  const displayDeals = dealsThisWeek.length > 0 ? dealsThisWeek : deals;

  if (displayDeals.length === 0) {
    return null;
  }

  const visibleDeals = showAll ? displayDeals : displayDeals.slice(0, PREVIEW_DEAL_COUNT);
  const liveCount = displayDeals.filter((deal) => getDealTiming(deal).state === 'live').length;
  const remainingCount = displayDeals.length - PREVIEW_DEAL_COUNT;

  return (
    <View className="mx-4 mt-7 gap-3">
      <View className="flex-row items-end justify-between gap-3">
        <View className="min-w-0 flex-1 gap-1">
          <Text variant="small" className="uppercase tracking-[1.6px] text-primary">
            Specials
          </Text>
          <Text className="text-xl font-bold text-foreground">Deals this week</Text>
        </View>
        <Text
          variant="small"
          className={liveCount > 0 ? 'font-semibold text-primary' : 'text-muted-foreground'}>
          {liveCount > 0 ? `${liveCount} live now` : `${displayDeals.length} listed`}
        </Text>
      </View>

      <View className="overflow-hidden rounded-3xl border border-border/60 bg-card/70">
        {visibleDeals.map((deal, index) => (
          <DealPreviewRow
            key={deal.id}
            deal={deal}
            isFirst={index === 0}
            isSavingDeal={isSavingDeal}
            onSave={onSave}
          />
        ))}

        {remainingCount > 0 ? (
          <View className="border-t border-border/50 px-3 py-3">
            <Button
              variant="outline"
              className="h-11 rounded-2xl"
              onPress={() => setShowAll((current) => !current)}>
              <Text>{showAll ? 'Show fewer deals' : `Show ${remainingCount} more deals`}</Text>
            </Button>
          </View>
        ) : null}
      </View>
    </View>
  );
}

function DealPreviewRow({
  deal,
  isFirst,
  isSavingDeal,
  onSave,
}: {
  deal: VenuePublicDeal;
  isFirst: boolean;
  isSavingDeal?: boolean;
  onSave?: (deal: VenuePublicDeal) => void;
}) {
  const timing = getDealTiming(deal);
  const isLive = timing.state === 'live';

  return (
    <View
      className={cn(
        'flex-row items-start gap-3 px-3.5 py-3.5',
        !isFirst && 'border-t border-border/50'
      )}>
      <View
        className={cn(
          'size-12 items-center justify-center rounded-2xl',
          isLive ? 'bg-primary' : 'bg-primary/12'
        )}>
        <Icon
          as={TicketPercentIcon}
          size={20}
          className={isLive ? 'text-primary-foreground' : 'text-primary'}
        />
      </View>
      <View className="min-w-0 flex-1 gap-1">
        <View className="flex-row flex-wrap items-center gap-x-2 gap-y-1">
          <Text
            variant="small"
            className={cn(
              'font-semibold uppercase tracking-[1.2px]',
              isLive ? 'text-primary' : 'text-muted-foreground'
            )}>
            {isLive ? 'Live now' : timing.label}
          </Text>
          <View className="flex-row items-center gap-1">
            <Icon as={Clock3Icon} size={12} className="text-muted-foreground" />
            <Text variant="small" className="text-muted-foreground">
              {formatDealTimeRange(deal)}
            </Text>
          </View>
        </View>
        <Text className="text-base font-semibold leading-5 text-foreground" numberOfLines={2}>
          {deal.title}
        </Text>
        <Text variant="small" className="leading-5 text-muted-foreground" numberOfLines={2}>
          {formatDealDays(deal.active_days)}
          {deal.description ? ` · ${deal.description}` : ''}
        </Text>
      </View>
      {onSave ? (
        <Button
          variant={deal.is_saved ? 'secondary' : 'outline'}
          size="icon"
          disabled={isSavingDeal}
          className="size-10 rounded-full"
          onPress={() => onSave(deal)}>
          <Icon
            as={deal.is_saved ? CheckIcon : BookmarkIcon}
            size={16}
            className={deal.is_saved ? 'text-secondary-foreground' : 'text-foreground'}
          />
        </Button>
      ) : null}
    </View>
  );
}
